import React, { FC, useRef } from 'react'
import styled from 'styled-components'
import { Fonts, ScrollPage } from 'components/base-ui'
import { displayPrice2Digit } from 'helpers'
import { Button } from 'components/base-ui'
import { useTranslation } from 'react-i18next'
import SummaryOrder from 'components/SummaryOrder'
import { OrderFpWithPromotionQuery, OrderHhWithPromotionQuery } from 'graphQL/dinein/generate/operations'
import ExVatInfo, { WrapperWithExVatInfo } from 'components/ExVatInfo'
import { useSelector } from 'react-redux'
import { vendorSelector } from 'store/slices/vendorSlice'

interface ReceiptItem {
  name: string
  totalSatang: number
}

type Props = {
  buffetItems: ReceiptItem[]
  lineItems: ReceiptItem[]
  buffetPackages: ReceiptItem[]
  adjustments:
    | OrderFpWithPromotionQuery['orderAndFPMemberCoupons']['order']['adjustments']
    | OrderHhWithPromotionQuery['orderAndHHMemberCoupons']['order']['adjustments']
  total: number
  onCheckout: () => void
}

const Receipt: FC<Props> = ({ buffetItems, lineItems, buffetPackages, total, onCheckout, adjustments }) => {
  const { t } = useTranslation()
  const scrollPageRef = useRef(null)
  const {
    setting: { excludingVat },
  } = useSelector(vendorSelector)

  const ItemList = ({ items }: { items: ReceiptItem[] }) => (
    <ItemsWrapper>
      {items.map((item, index) => (
        <Item key={index}>
          <Name>{item.name}</Name>
          <Price>{displayPrice2Digit(item.totalSatang / 100)}</Price>
        </Item>
      ))}
    </ItemsWrapper>
  )

  return (
    <ScrollPage id="receipt-overlay" ref={scrollPageRef}>
      <Wrapper id="order-receipt">
        <Header>
          <Fonts.Large>{t('buffet.receipt.title')}</Fonts.Large>
          <Fonts.BaseTitleRegular style={{ fontSize: 14 }}>{t('buffet.receipt.description')}</Fonts.BaseTitleRegular>
        </Header>
        <Divider />
        <ItemList items={buffetPackages} />
        {lineItems.length > 0 && (
          <>
            <Divider />
            <ItemList items={lineItems} />
          </>
        )}
        {buffetItems.length > 0 && (
          <>
            <Divider />
            <ItemList items={buffetItems} />
          </>
        )}
        {adjustments.length > 0 && (
          <>
            <SummaryOrderWrapper>
              <SummaryOrder adjustments={adjustments} />
            </SummaryOrderWrapper>
          </>
        )}
        <Divider />
        <Footer id="receipt-footer">
          <ItemsWrapper style={{ padding: 0 }}>
            <Item>
              <Fonts.Medium> {t('buffet.receipt.total')}</Fonts.Medium>
              <Fonts.Medium>{displayPrice2Digit(total / 100)}</Fonts.Medium>
            </Item>
          </ItemsWrapper>
          <WrapperWithExVatInfo>
            <CheckoutButton onClick={onCheckout}>{t('buffet.globalCartPage.button.checkout')}</CheckoutButton>
            {excludingVat && <ExVatInfo />}
          </WrapperWithExVatInfo>
        </Footer>
      </Wrapper>
    </ScrollPage>
  )
}

export default Receipt

const Wrapper = styled.div`
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 155px;
  background-color: ${({ theme }) => theme.colors.white};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 12px;
  text-align: center;
  padding-bottom: 24px;
`

const Divider = styled.div`
  height: 0.5px;
  width: 100%;

  position: relative;

  &::before {
    content: '';
    display: block;
    height: 1px;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.baseGrey};
    opacity: 0.5;
  }
`

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding-top: 15px;
  padding-bottom: 15px;
  width: 100%;
`

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  column-gap: 8px;
`

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 22px;
  width: 100%;
  padding: 25px 28px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px -2px 10px #f1f2f6;
`

const CheckoutButton = styled(Button)`
  min-height: 50px;
  border-radius: 85px;
  align-self: center;
`

const Name = styled(Fonts.BaseTitleRegular)``
const Price = styled(Fonts.BaseTitleRegular)`
  text-align: right;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SummaryOrderWrapper = styled.div`
  width: 100%;
`
